import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhoneGatewayCard from "@/components/PhoneGatewayCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhoneCall, MessageCircle, ShieldCheck, Save, CheckCircle2, KeyRound, UserPlus, Users, Trash2, Calendar, LifeBuoy, Copy } from "lucide-react";
import PhonePeCard from "@/components/PhonePeCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Owner-only Settings page. WhatsApp Business + Exotel telephony configs.
export default function OwnerSettings() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    provider: "",
    business_number: "",
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_from: "",
    gupshup_api_key: "",
    gupshup_app_name: "",
    gupshup_source_number: "",
  });

  // Exotel state
  const [exoCfg, setExoCfg] = useState(null);
  const [exoEditing, setExoEditing] = useState(false);
  const [exoForm, setExoForm] = useState({
    sid: "",
    api_key: "",
    api_token: "",
    exophone: "",
    subdomain: "api.in.exotel.com",
  });
  const [exoTesting, setExoTesting] = useState(false);

  // Account security state
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [changingPwd, setChangingPwd] = useState(false);

  // Add-admin state
  const [adminForm, setAdminForm] = useState({ name: "", email: "", password: "", phone: "", validity_expires_at: "" });

  // Shop customer password reset — owner types a mobile, we mint a temp
  // password and show it once so they can share it with the customer.
  const [custResetMobile, setCustResetMobile] = useState("");
  const [custResetBusy, setCustResetBusy] = useState(false);
  const [custResetResult, setCustResetResult] = useState(null);

  const resetShopCustomerPwd = async (e) => {
    e?.preventDefault();
    const m = (custResetMobile || "").trim();
    if (m.length < 10) {
      toast.error("Enter the customer's full mobile number.");
      return;
    }
    setCustResetBusy(true);
    setCustResetResult(null);
    try {
      const { data } = await api.post("/store/customers/reset-password", { mobile: m });
      setCustResetResult(data);
      toast.success(`Temporary password created for ${data.customer_name || "customer"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset that customer's password.");
    } finally {
      setCustResetBusy(false);
    }
  };

  const copyTempPwd = async () => {
    if (!custResetResult?.temp_password) return;
    try {
      await navigator.clipboard.writeText(custResetResult.temp_password);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy failed — please select and copy manually.");
    }
  };
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminTab, setAdminTab] = useState("active");
  const [deletingAdmin, setDeletingAdmin] = useState(null);

  // Preferred-dialer state — pick which app opens when clicking a phone.
  const [dialerPref, setDialerPref] = useState({
    preferred_dialer: "system",
    custom_dialer_scheme: "",
  });
  const [dialerSaving, setDialerSaving] = useState(false);

  // Razorpay per-tenant credentials — customer payments route through the
  // shop's own Razorpay account so money lands in the owner's bank.
  const [rzpCfg, setRzpCfg] = useState({
    configured: false,
    using_env_fallback: false,
    key_id: "",
    effective_key_id_preview: "",
    secret_set: false,
  });
  const [rzpForm, setRzpForm] = useState({ key_id: "", key_secret: "" });
  const [rzpSaving, setRzpSaving] = useState(false);
  const [rzpTesting, setRzpTesting] = useState(false);

  const loadRzp = async () => {
    try {
      const { data } = await api.get("/store/razorpay-config");
      setRzpCfg(data);
      setRzpForm({ key_id: data.key_id || "", key_secret: "" });
    } catch (e) {
      /* handled by interceptor */
    }
  };

  const saveRzp = async () => {
    if (!rzpForm.key_id.trim()) {
      toast.error("Enter your Razorpay Key ID.");
      return;
    }
    setRzpSaving(true);
    try {
      const payload = { key_id: rzpForm.key_id.trim() };
      // Only send the secret if the admin actually typed a new one — an empty
      // input preserves whatever's already stored (mirrors WhatsApp/Exotel UX).
      if (rzpForm.key_secret.trim()) payload.key_secret = rzpForm.key_secret.trim();
      const { data } = await api.put("/store/razorpay-config", payload);
      setRzpCfg(data);
      setRzpForm({ key_id: data.key_id || "", key_secret: "" });
      toast.success("Razorpay keys saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save Razorpay keys");
    } finally {
      setRzpSaving(false);
    }
  };

  const testRzp = async () => {
    setRzpTesting(true);
    try {
      const { data } = await api.get("/store/razorpay-config/test");
      toast.success(data.message || "Razorpay keys work.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Razorpay rejected these keys.");
    } finally {
      setRzpTesting(false);
    }
  };

  const clearRzp = async () => {
    if (!window.confirm("Remove Razorpay keys? Customers won't be able to prepay online.")) return;
    try {
      await api.delete("/store/razorpay-config");
      await loadRzp();
      toast.success("Razorpay keys cleared");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear Razorpay keys");
    }
  };

  const saveDialerPref = async () => {
    setDialerSaving(true);
    try {
      const payload = {
        preferred_dialer: dialerPref.preferred_dialer,
        custom_dialer_scheme: dialerPref.custom_dialer_scheme || null,
      };
      await api.put("/auth/preferences", payload);
      // Bust the DialButton cache so the next click uses the new choice.
      const { invalidateDialerPrefs } = await import("@/components/DialButton");
      invalidateDialerPrefs();
      toast.success("Dialer preference saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save dialer preference");
    } finally {
      setDialerSaving(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/whatsapp/config");
      setCfg(data);
      setForm((f) => ({
        ...f,
        provider: data.provider || "",
        business_number: data.business_number || "",
        twilio_from: data.twilio_from || "",
        gupshup_app_name: data.gupshup_app_name || "",
        gupshup_source_number: data.gupshup_source_number || "",
      }));
    } catch (e) {
      /* handled by interceptor */
    }
    try {
      const { data } = await api.get("/exotel/config");
      setExoCfg(data);
      setExoForm((f) => ({
        ...f,
        sid: data.sid || "",
        exophone: data.exophone || "",
        subdomain: data.subdomain || "api.in.exotel.com",
      }));
    } catch (e) {
      /* handled by interceptor */
    }
    try {
      const { data } = await api.get("/auth/preferences");
      setDialerPref({
        preferred_dialer: data.preferred_dialer || "system",
        custom_dialer_scheme: data.custom_dialer_scheme || "",
      });
    } catch (e) {
      /* handled by interceptor */
    }
    await loadRzp();
    if (isSuperAdmin) {
      try {
        const { data } = await api.get("/auth/admins");
        setAdmins(data || []);
      } catch (e) {
        /* handled by interceptor */
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const payload = { ...form };
      // don't overwrite secrets if user left them blank
      if (!payload.twilio_account_sid) delete payload.twilio_account_sid;
      if (!payload.twilio_auth_token) delete payload.twilio_auth_token;
      if (!payload.gupshup_api_key) delete payload.gupshup_api_key;
      await api.post("/whatsapp/config", payload);
      toast.success("WhatsApp config saved");
      setForm((f) => ({
        ...f,
        twilio_account_sid: "",
        twilio_auth_token: "",
        gupshup_api_key: "",
      }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Exotel handlers
  const setExo = (k) => (e) =>
    setExoForm({ ...exoForm, [k]: e.target.value });

  const saveExo = async () => {
    try {
      const payload = { ...exoForm };
      // Blank secret fields shouldn't overwrite existing values.
      if (!payload.api_key) delete payload.api_key;
      if (!payload.api_token) delete payload.api_token;
      await api.post("/exotel/config", payload);
      toast.success("Exotel credentials saved");
      setExoForm((f) => ({ ...f, api_key: "", api_token: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const testExo = async () => {
    setExoTesting(true);
    try {
      const { data } = await api.post("/exotel/test");
      toast.success(
        data.balance != null
          ? `✅ Connected. Balance: ₹${data.balance}`
          : "✅ Exotel credentials work."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify");
    } finally {
      setExoTesting(false);
    }
  };

  const changePassword = async () => {
    if (!pwd.current_password || !pwd.new_password) {
      toast.error("Fill both current and new password.");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    setChangingPwd(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed. Use the new one on your next sign-in.");
      setPwd({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setChangingPwd(false);
    }
  };

  const createAdmin = async () => {
    const { name, email, password, phone, validity_expires_at } = adminForm;
    if (!name || !email || !password) {
      toast.error("Name, email and password are required.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setAddingAdmin(true);
    try {
      await api.post("/auth/admins", {
        name, email, password, phone,
        validity_expires_at: validity_expires_at || null,
      });
      toast.success(`Admin ${name} created — they can sign in with ${email}`);
      setAdminForm({ name: "", email: "", password: "", phone: "", validity_expires_at: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create admin");
    } finally {
      setAddingAdmin(false);
    }
  };

  const setValidity = (admin) => {
    // Open the dedicated dialog — much friendlier than window.prompt() which
    // was blocked/ugly on mobile browsers and had zero affordance.
    setValidityAdmin(admin);
    setValidityDate(
      admin.validity_expires_at
        ? String(admin.validity_expires_at).slice(0, 10)
        : "",
    );
  };

  // Validity-editing dialog state (replaces the old window.prompt flow).
  const [validityAdmin, setValidityAdmin] = useState(null);
  const [validityDate, setValidityDate] = useState("");
  const [validitySaving, setValiditySaving] = useState(false);

  const closeValidityDialog = () => {
    setValidityAdmin(null);
    setValidityDate("");
  };

  const saveValidity = async (dateStr) => {
    if (!validityAdmin) return;
    setValiditySaving(true);
    try {
      await api.patch(`/auth/admins/${validityAdmin.id}/validity`, {
        validity_expires_at: dateStr ? dateStr : null,
      });
      toast.success(dateStr ? `Validity set to ${dateStr}` : "Validity cleared");
      closeValidityDialog();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update validity");
    } finally {
      setValiditySaving(false);
    }
  };

  const quickExtend = (days) => {
    const base = validityDate ? new Date(validityDate) : new Date();
    if (isNaN(base.getTime())) return;
    base.setDate(base.getDate() + days);
    setValidityDate(base.toISOString().slice(0, 10));
  };

  const deleteAdmin = async (admin) => {
    setDeletingAdmin(admin.id);
    try {
      const { data } = await api.delete(`/auth/admins/${admin.id}`);
      const removed = Object.values(data.counts || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(
        `Deleted admin ${admin.email} and ${removed} related records.`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingAdmin(null);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[900px]" data-testid="owner-settings">
      <div className="mb-8">
        <div className="mono text-xs uppercase tracking-widest text-[#FF5000]">
          Settings
        </div>
        <h1 className="font-heading text-3xl font-bold mt-2">Preferred dialer</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Choose which app opens when you tap the green phone icon next to any
          number in DialFlow. Perfect if your team uses a cloud-calling app
          like Heyo Phone or Truecaller instead of the built-in dialer.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none" data-testid="dialer-pref-card">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-emerald-600" />
            Which dialer opens on tap?
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label>App to open</Label>
            <Select
              value={dialerPref.preferred_dialer}
              onValueChange={(v) => setDialerPref({ ...dialerPref, preferred_dialer: v })}
            >
              <SelectTrigger data-testid="dialer-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="system">System dialer (default phone app)</SelectItem>
                <SelectItem value="heyo">Heyo Phone (cloud calling)</SelectItem>
                <SelectItem value="truecaller">Truecaller</SelectItem>
                <SelectItem value="custom">Custom URL scheme...</SelectItem>
              </SelectContent>
            </Select>
            <p className="mono text-[10px] text-slate-500">
              System dialer works everywhere. Heyo / Truecaller require the app
              installed on the phone with its URL scheme registered.
            </p>
          </div>

          {dialerPref.preferred_dialer === "custom" && (
            <div className="space-y-2 border-l-4 border-l-fuchsia-400 bg-fuchsia-50/40 rounded p-4">
              <Label>Custom URL scheme</Label>
              <Input
                value={dialerPref.custom_dialer_scheme}
                onChange={(e) => setDialerPref({ ...dialerPref, custom_dialer_scheme: e.target.value })}
                placeholder="e.g. myapp://call/{number}"
                data-testid="dialer-custom-scheme"
              />
              <p className="mono text-[10px] text-slate-500">
                Must contain <span className="text-slate-800">{"{number}"}</span> where the digits go.
                Example: <span className="text-slate-800">exotelvoice://call/{"{number}"}</span>
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={saveDialerPref}
              disabled={dialerSaving}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="dialer-save"
            >
              <Save className="w-4 h-4 mr-1" />
              {dialerSaving ? "Saving..." : "Save dialer preference"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-14 mb-6">
        <h1 className="font-heading text-3xl font-bold">Online Payments</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Accept UPI / card / netbanking on your storefront via Razorpay or
          PhonePe. Money settles directly to the bank account linked to your
          gateway account. DialFlow never touches your funds. If both are set
          up, customers can choose at checkout.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none" data-testid="razorpay-card">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-indigo-600" />
            Razorpay credentials
            {rzpCfg.configured && (
              <span className="mono text-[10px] uppercase text-emerald-700 flex items-center gap-1 ml-2">
                <ShieldCheck className="w-3 h-3" /> configured
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="text-xs text-slate-500 leading-relaxed">
            Get your keys from{" "}
            <a
              href="https://dashboard.razorpay.com/app/keys"
              target="_blank"
              rel="noreferrer"
              className="text-[#0055FF] underline"
            >
              dashboard.razorpay.com → Settings → API Keys
            </a>
            . Use <span className="mono">rzp_test_…</span> keys while trying
            things out; switch to <span className="mono">rzp_live_…</span> once
            you&apos;re KYC-approved and ready to charge real customers.
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="rzp-key-id">Key ID</Label>
              <Input
                id="rzp-key-id"
                placeholder="rzp_live_XXXXXXXXXXXXXX"
                value={rzpForm.key_id}
                onChange={(e) => setRzpForm({ ...rzpForm, key_id: e.target.value })}
                className="mono text-xs"
                data-testid="rzp-key-id"
              />
              {rzpCfg.effective_key_id_preview && !rzpForm.key_id && (
                <div className="mono text-[10px] text-slate-500">
                  Currently active: {rzpCfg.effective_key_id_preview}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rzp-key-secret">Key Secret</Label>
              <Input
                id="rzp-key-secret"
                type="password"
                placeholder={
                  rzpCfg.secret_set
                    ? "•••••••••••• (leave blank to keep saved secret)"
                    : "Paste your Razorpay secret"
                }
                value={rzpForm.key_secret}
                onChange={(e) =>
                  setRzpForm({ ...rzpForm, key_secret: e.target.value })
                }
                className="mono text-xs"
                data-testid="rzp-key-secret"
                autoComplete="new-password"
              />
              <div className="mono text-[10px] text-slate-500">
                Secret is stored securely and never sent back to the browser.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              onClick={saveRzp}
              disabled={rzpSaving}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="rzp-save"
            >
              <Save className="w-4 h-4 mr-1" />
              {rzpSaving ? "Saving..." : "Save Razorpay keys"}
            </Button>
            <Button
              variant="outline"
              onClick={testRzp}
              disabled={rzpTesting || !rzpCfg.configured}
              data-testid="rzp-test"
            >
              {rzpTesting ? "Testing..." : "Test connection"}
            </Button>
            {rzpCfg.configured && (
              <Button
                variant="ghost"
                onClick={clearRzp}
                className="text-red-600 hover:text-red-700"
                data-testid="rzp-clear"
              >
                Remove keys
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <PhonePeCard />

      <div className="mt-14 mb-6">
        <h1 className="font-heading text-3xl font-bold">Company WhatsApp</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Route all outgoing/incoming WhatsApp messages through your business
          number — employees never use their personal WhatsApp.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            WhatsApp Business Provider
            {cfg?.configured && (
              <span className="mono text-[10px] uppercase text-emerald-700 flex items-center gap-1 ml-2">
                <ShieldCheck className="w-3 h-3" /> configured
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select
                value={form.provider}
                onValueChange={(v) => setForm({ ...form, provider: v })}
              >
                <SelectTrigger data-testid="wa-provider">
                  <SelectValue placeholder="Choose provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="twilio">Twilio</SelectItem>
                  <SelectItem value="gupshup">Gupshup</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Company WhatsApp number (E.164)</Label>
              <Input
                placeholder="+91XXXXXXXXXX"
                value={form.business_number}
                onChange={set("business_number")}
                data-testid="wa-business-number"
              />
            </div>
          </div>

          {form.provider === "twilio" && (
            <div className="border-l-4 border-l-[#0055FF] bg-blue-50/40 p-4 rounded space-y-3">
              <div className="mono text-[10px] uppercase text-[#0055FF]">
                Twilio credentials
              </div>
              {(form.twilio_from || cfg?.twilio_from || "").includes("14155238886") && (
                <div className="border border-amber-300 bg-amber-50 rounded p-3 mono text-[11px] text-amber-900">
                  <div className="font-semibold text-amber-900 mb-1">
                    ⚠ Twilio Sandbox opt-in required (before ANY message will reach a phone)
                  </div>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>From <em>each recipient phone</em>, open WhatsApp and send a message to <span className="text-slate-800">+1 415 523 8886</span>.</li>
                    <li>The message body must be <span className="text-slate-800">join &lt;your-two-word-code&gt;</span> — find your exact code at twilio.com/console/sms/whatsapp/learn.</li>
                    <li>Twilio replies with &ldquo;Connected to sandbox&rdquo; — that phone can now receive DialFlow messages for 72 hours.</li>
                  </ol>
                  <div className="mt-2">Move to an approved WhatsApp Business sender (production) to skip this step entirely.</div>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Account SID {cfg?.twilio_account_sid && <span className="text-slate-400 mono text-[10px]">current: {cfg.twilio_account_sid}</span>}</Label>
                  <Input
                    value={form.twilio_account_sid}
                    onChange={set("twilio_account_sid")}
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    data-testid="wa-twilio-sid"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Auth Token {cfg?.twilio_auth_token && <span className="text-slate-400 mono text-[10px]">current: {cfg.twilio_auth_token}</span>}</Label>
                  <Input
                    type="password"
                    value={form.twilio_auth_token}
                    onChange={set("twilio_auth_token")}
                    data-testid="wa-twilio-token"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>WhatsApp sender number (Twilio &quot;From&quot;)</Label>
                <Input
                  value={form.twilio_from}
                  onChange={set("twilio_from")}
                  placeholder="+14155238886 (Twilio sandbox) or your approved sender"
                  data-testid="wa-twilio-from"
                />
              </div>
              <p className="mono text-[10px] text-slate-500">
                Webhook URL for Twilio: <span className="text-slate-800">{`${window.location.origin}/api/whatsapp/webhook/twilio`}</span>
              </p>
            </div>
          )}

          {form.provider === "gupshup" && (
            <div className="border-l-4 border-l-emerald-500 bg-emerald-50/40 p-4 rounded space-y-3">
              <div className="mono text-[10px] uppercase text-emerald-700">
                Gupshup credentials
              </div>
              <div className="space-y-2">
                <Label>API Key {cfg?.gupshup_api_key && <span className="text-slate-400 mono text-[10px]">current: {cfg.gupshup_api_key}</span>}</Label>
                <Input
                  type="password"
                  value={form.gupshup_api_key}
                  onChange={set("gupshup_api_key")}
                  data-testid="wa-gupshup-key"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>App name</Label>
                  <Input
                    value={form.gupshup_app_name}
                    onChange={set("gupshup_app_name")}
                    data-testid="wa-gupshup-app"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Source number (E.164)</Label>
                  <Input
                    value={form.gupshup_source_number}
                    onChange={set("gupshup_source_number")}
                    placeholder="919XXXXXXXXX"
                    data-testid="wa-gupshup-source"
                  />
                </div>
              </div>
              <p className="mono text-[10px] text-slate-500">
                Webhook URL for Gupshup: <span className="text-slate-800">{`${window.location.origin}/api/whatsapp/webhook/gupshup`}</span>
              </p>
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={save}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="wa-save"
            >
              <Save className="w-4 h-4 mr-1" /> Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6 text-sm text-slate-600 space-y-2">
        <p>
          <strong>Note:</strong> The company WhatsApp number must be dedicated —
          Meta forbids using the same number on the personal WhatsApp app.
        </p>
        <p>
          When configured, employees see a &quot;WhatsApp&quot; button on every task that
          sends messages from your company number. Replies come back into
          DialFlow via the webhook above.
        </p>
      </div>

      {/* ---------- Exotel telephony section ---------- */}
      <div className="mt-14 mb-6">
        <h1 className="font-heading text-3xl font-bold">Exotel telephony</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Paste your Exotel credentials so employees&apos; taps of &ldquo;Call&rdquo; ring
          their phone first, then bridge to the customer &mdash; recording every
          call automatically.
        </p>
      </div>

      <Card className="border-slate-200 shadow-none" data-testid="exotel-card">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <PhoneCall className="w-5 h-5 text-[#0055FF]" />
            Exotel account
            {exoCfg?.sid && !exoEditing && (
              <span className="mono text-[10px] uppercase text-emerald-700 flex items-center gap-1 ml-2">
                <ShieldCheck className="w-3 h-3" /> credentials saved
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-5">
          {/* SAVED VIEW — shown when creds exist AND not editing */}
          {exoCfg?.sid && !exoEditing ? (
            <>
              <div className="border-l-4 border-l-emerald-500 bg-emerald-50/60 rounded p-4 space-y-2">
                <div className="flex items-center gap-2 font-heading text-emerald-800">
                  <CheckCircle2 className="w-5 h-5" />
                  Your Exotel credentials are saved
                </div>
                <div className="mono text-[11px] text-slate-700 grid md:grid-cols-2 gap-x-6 gap-y-1 mt-2">
                  <div>
                    <span className="text-slate-500">Account SID: </span>
                    <span className="text-slate-900">{exoCfg.sid}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">ExoPhone: </span>
                    <span className="text-slate-900">
                      {exoCfg.exophone || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">API Key: </span>
                    <span className="text-slate-900">
                      {exoCfg.api_key || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">API Token: </span>
                    <span className="text-slate-900">
                      {exoCfg.api_token || "—"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Subdomain: </span>
                    <span className="text-slate-900">{exoCfg.subdomain}</span>
                  </div>
                </div>
                <div className="mono text-[10px] text-slate-500 mt-2">
                  Secrets are stored encrypted and never sent back to your
                  browser in plain text.
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={testExo}
                  disabled={exoTesting}
                  data-testid="exo-test"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {exoTesting ? "Testing…" : "Test connection"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setExoEditing(true)}
                  data-testid="exo-edit"
                >
                  Change credentials
                </Button>
              </div>
            </>
          ) : (
            // EDIT VIEW — new user OR clicked "Change credentials"
            <>
              <div className="border border-blue-200 bg-blue-50/60 rounded p-3 mono text-[11px] text-slate-700 space-y-1">
                <div className="font-semibold text-[#0055FF] mb-1">
                  Where do I find these?
                </div>
                <ol className="list-decimal list-inside space-y-1">
                  <li>
                    Log in to{" "}
                    <a
                      href="https://my.exotel.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline text-[#0055FF]"
                    >
                      my.exotel.com
                    </a>{" "}
                    (once KYC is approved).
                  </li>
                  <li>
                    Settings &rarr; API &mdash; you&apos;ll see{" "}
                    <span className="text-slate-800">Account SID</span>,{" "}
                    <span className="text-slate-800">API Key</span> and{" "}
                    <span className="text-slate-800">API Token</span>.
                  </li>
                  <li>
                    ExoPhone: your virtual number, listed on the ExoPhones page.
                  </li>
                  <li>
                    Subdomain: use{" "}
                    <span className="text-slate-800">api.in.exotel.com</span>{" "}
                    for India accounts (cheaper), or{" "}
                    <span className="text-slate-800">api.exotel.com</span>{" "}
                    global.
                  </li>
                </ol>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Account SID</Label>
                  <Input
                    value={exoForm.sid}
                    onChange={setExo("sid")}
                    placeholder="e.g. yourcompany1"
                    data-testid="exo-sid"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subdomain</Label>
                  <Select
                    value={exoForm.subdomain}
                    onValueChange={(v) =>
                      setExoForm({ ...exoForm, subdomain: v })
                    }
                  >
                    <SelectTrigger data-testid="exo-subdomain">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="api.in.exotel.com">
                        api.in.exotel.com &nbsp;(India)
                      </SelectItem>
                      <SelectItem value="api.exotel.com">
                        api.exotel.com &nbsp;(Global)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    API Key{" "}
                    {exoCfg?.api_key && (
                      <span className="text-slate-400 mono text-[10px]">
                        saved: {exoCfg.api_key}
                      </span>
                    )}
                  </Label>
                  <Input
                    value={exoForm.api_key}
                    onChange={setExo("api_key")}
                    placeholder={
                      exoCfg?.api_key
                        ? "leave blank to keep saved value"
                        : "paste your API key"
                    }
                    data-testid="exo-api-key"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    API Token{" "}
                    {exoCfg?.api_token && (
                      <span className="text-slate-400 mono text-[10px]">
                        saved: {exoCfg.api_token}
                      </span>
                    )}
                  </Label>
                  <Input
                    type="password"
                    value={exoForm.api_token}
                    onChange={setExo("api_token")}
                    placeholder={
                      exoCfg?.api_token
                        ? "leave blank to keep saved value"
                        : "paste your API token"
                    }
                    data-testid="exo-api-token"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>ExoPhone (virtual number)</Label>
                <Input
                  value={exoForm.exophone}
                  onChange={setExo("exophone")}
                  placeholder="e.g. 08048xxxxxx"
                  data-testid="exo-exophone"
                />
                <p className="mono text-[10px] text-slate-500">
                  Buy this on the Exotel dashboard. This is the number customers
                  see when your employees call them.
                </p>
              </div>

              <div className="flex justify-end pt-2 gap-2">
                {exoCfg?.sid && (
                  <Button
                    variant="outline"
                    onClick={() => setExoEditing(false)}
                    data-testid="exo-edit-cancel"
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  onClick={saveExo}
                  className="bg-[#0055FF] hover:bg-[#0040CC]"
                  data-testid="exo-save"
                >
                  <Save className="w-4 h-4 mr-1" /> Save
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 mb-10 text-sm text-slate-600 space-y-2">
        <p>
          <strong>KYC still pending?</strong> Once Exotel emails you the
          approval &amp; the credentials, come back here, paste them, click
          &quot;Save&quot; and then &quot;Test connection&quot;. If the balance shows &mdash;
          you&apos;re live.
        </p>
      </div>

      {/* ---------- Phone gateway (Raspberry Pi + SIM HAT) ---------- */}
      <PhoneGatewayCard />

      {/* ---------- Account security ---------- */}
      <div className="mt-14 mb-6">
        <h1 className="font-heading text-3xl font-bold">Account security</h1>
        <p className="text-slate-500 mt-1 text-sm">
          {isSuperAdmin
            ? "Manage your admin password and add new admin accounts."
            : "Change your password. Use at least 8 characters and something you haven't used elsewhere."}
        </p>
      </div>

      <div className={"grid gap-6 " + (isSuperAdmin ? "md:grid-cols-2" : "md:max-w-lg")}>
        <Card className="border-slate-200 shadow-none" data-testid="change-password-card">
          <CardHeader className="border-b">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-[#0055FF]" />
              Change password
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label>Current password</Label>
              <Input
                type="password"
                value={pwd.current_password}
                onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                placeholder="Enter your current password"
                data-testid="pwd-current"
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New password</Label>
              <Input
                type="password"
                value={pwd.new_password}
                onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                placeholder="At least 8 characters"
                data-testid="pwd-new"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm new password</Label>
              <Input
                type="password"
                value={pwd.confirm_password}
                onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
                placeholder="Type the new password again"
                data-testid="pwd-confirm"
                autoComplete="new-password"
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={changePassword}
                disabled={changingPwd}
                className="bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="pwd-submit"
              >
                <KeyRound className="w-4 h-4 mr-1" />
                {changingPwd ? "Updating…" : "Update password"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isSuperAdmin && (
        <Card className="border-slate-200 shadow-none" data-testid="add-admin-card">
          <CardHeader className="border-b">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-[#0055FF]" />
              Add another admin
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="border-l-4 border-l-amber-400 bg-amber-50/60 rounded p-3 mono text-[11px] text-slate-700">
              A new admin gets their own workspace — separate employees,
              tasks and customers. This is not a co-owner of your workspace.
            </div>
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                placeholder="e.g. Ravi Kumar"
                data-testid="admin-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="ravi@company.com"
                data-testid="admin-email"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone (optional)</Label>
              <Input
                value={adminForm.phone}
                onChange={(e) => setAdminForm({ ...adminForm, phone: e.target.value })}
                placeholder="+91…"
                data-testid="admin-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Starter password</Label>
              <Input
                type="password"
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                placeholder="At least 8 characters"
                data-testid="admin-password"
                autoComplete="new-password"
              />
              <p className="mono text-[10px] text-slate-500">
                Share this with the new admin. They can change it after
                signing in.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Validity expires on <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
              <Input
                type="date"
                value={adminForm.validity_expires_at}
                onChange={(e) => setAdminForm({ ...adminForm, validity_expires_at: e.target.value })}
                data-testid="admin-validity"
                min={new Date().toISOString().slice(0, 10)}
              />
              <p className="mono text-[10px] text-slate-500">
                The admin is auto-disabled at the end of this day. Leave blank for no expiry.
              </p>
            </div>
            <div className="flex justify-end pt-2">
              <Button
                onClick={createAdmin}
                disabled={addingAdmin}
                className="bg-[#0055FF] hover:bg-[#0040CC]"
                data-testid="admin-submit"
              >
                <UserPlus className="w-4 h-4 mr-1" />
                {addingAdmin ? "Creating…" : "Create admin"}
              </Button>
            </div>
          </CardContent>
        </Card>
        )}
      </div>

      {/* Admin list — super admin only. Splits into Active + Disabled tabs. */}
      {isSuperAdmin && (
      <Card
        className="border-slate-200 shadow-none mt-6 mb-16"
        data-testid="admins-list-card"
      >
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-[#0055FF]" />
            All admins ({admins.length})
          </CardTitle>
          <div className="flex gap-2 mt-3">
            {[["active", `Active (${admins.filter((a) => a.status !== "disabled").length})`],
              ["disabled", `Disabled (${admins.filter((a) => a.status === "disabled").length})`]].map(([k, lbl]) => (
              <button
                key={k}
                onClick={() => setAdminTab(k)}
                className={
                  "mono text-xs px-3 py-1 rounded-md " +
                  (adminTab === k
                    ? (k === "active" ? "bg-emerald-600 text-white" : "bg-red-600 text-white")
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                }
                data-testid={`admins-tab-${k}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {admins.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              Loading admins…
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3">Name</th>
                  <th className="text-left px-5 py-3">Email</th>
                  <th className="text-left px-5 py-3">Validity</th>
                  <th className="text-left px-5 py-3">Emps</th>
                  <th className="text-left px-5 py-3">Tasks</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {admins
                  .filter((a) => adminTab === "active" ? a.status !== "disabled" : a.status === "disabled")
                  .map((a) => {
                    const d = a.days_until_expiry;
                    const validity = a.validity_expires_at
                      ? new Date(a.validity_expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                      : "";
                    return (
                  <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium">{a.name}</div>
                      {a.is_you && (
                        <span className="mono text-[10px] uppercase text-[#0055FF]">
                          (you)
                        </span>
                      )}
                      {a.is_super_admin && (
                        <span className="mono text-[10px] uppercase text-fuchsia-700 ml-1">super</span>
                      )}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {a.email}
                    </td>
                    <td className="px-5 py-3 mono text-xs">
                      {a.is_super_admin ? (
                        <span className="text-slate-400">never expires</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setValidity(a)}
                          className="text-left w-full hover:bg-blue-50 rounded px-1 py-0.5 transition"
                          data-testid={`admin-validity-cell-${a.id}`}
                          title="Click to change validity"
                        >
                          {a.validity_expires_at ? (
                            <div>
                              <div className={
                                a.status === "disabled"
                                  ? "text-red-700"
                                  : (d != null && d <= 7 ? "text-amber-700 font-semibold" : "text-slate-700")
                              }>
                                {validity}
                              </div>
                              {a.status === "disabled" && a.disabled_reason === "validity_expired" ? (
                                <div className="text-[10px] text-red-600">expired · click to renew</div>
                              ) : d != null && d <= 7 ? (
                                <div className="text-[10px] text-amber-600" data-testid={`admin-expiry-warn-${a.id}`}>
                                  expires in {d} day{d === 1 ? "" : "s"}
                                </div>
                              ) : (
                                <div className="text-[10px] text-slate-400">click to change</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 underline decoration-dotted underline-offset-2">
                              set expiry
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {a.employee_count}
                    </td>
                    <td className="px-5 py-3 mono text-xs text-slate-700">
                      {a.task_count}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {!a.is_super_admin && (
                          <button
                            type="button"
                            onClick={() => setValidity(a)}
                            className="text-slate-400 hover:text-[#0055FF] hover:bg-blue-50 rounded p-1 transition-colors"
                            title="Set validity"
                            data-testid={`admin-set-validity-${a.id}`}
                          >
                            <Calendar className="w-4 h-4" />
                          </button>
                        )}
                        {a.is_you ? (
                          <span className="mono text-[10px] text-slate-400 ml-1">
                            protected
                          </span>
                        ) : (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button
                                type="button"
                                disabled={deletingAdmin === a.id}
                                className="text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-1 transition-colors disabled:opacity-50"
                                title={`Delete admin ${a.email}`}
                                data-testid={`admin-delete-${a.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="flex items-center gap-2">
                                <Trash2 className="w-5 h-5 text-red-500" />
                                Delete admin {a.name}?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                This <strong>permanently deletes</strong>{" "}
                                everything in{" "}
                                <span className="mono text-slate-800">
                                  {a.email}
                                </span>
                                &apos;s workspace: {a.employee_count}{" "}
                                employees, {a.task_count} tasks, all their
                                call recordings, customer contacts, attendance
                                records and WhatsApp / Exotel settings.
                                <br />
                                <br />
                                This <strong>cannot be undone</strong>.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                data-testid={`admin-delete-cancel-${a.id}`}
                              >
                                Cancel
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteAdmin(a)}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid={`admin-delete-confirm-${a.id}`}
                              >
                                Yes, delete admin
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      )}

      {/* Validity-set dialog — replaces the old browser prompt so it works
          on mobile and gives super-admins a proper date picker + quick
          "extend by N days" shortcuts. Kept outside the isSuperAdmin
          fragment so JSX stays a single expression; the dialog only
          opens when validityAdmin state is set (super-admin action). */}
      <Dialog open={!!validityAdmin} onOpenChange={(v) => { if (!v) closeValidityDialog(); }}>
        <DialogContent className="max-w-sm" data-testid="admin-validity-dialog">
          <DialogHeader>
            <DialogTitle>Set validity</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {validityAdmin?.name || validityAdmin?.email} will be
              auto-disabled at the end of this date.
              Leave blank for no expiry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="validity-date-input">Expires on</Label>
              <Input
                id="validity-date-input"
                type="date"
                value={validityDate}
                onChange={(e) => setValidityDate(e.target.value)}
                min={new Date().toISOString().slice(0, 10)}
                data-testid="admin-validity-date-input"
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => quickExtend(30)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
                data-testid="admin-validity-plus30"
              >
                +30 days
              </button>
              <button
                type="button"
                onClick={() => quickExtend(90)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
                data-testid="admin-validity-plus90"
              >
                +90 days
              </button>
              <button
                type="button"
                onClick={() => quickExtend(180)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
                data-testid="admin-validity-plus180"
              >
                +180 days
              </button>
              <button
                type="button"
                onClick={() => quickExtend(365)}
                className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200"
                data-testid="admin-validity-plus365"
              >
                +1 year
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => saveValidity("")}
              disabled={validitySaving}
              className="text-red-600 hover:text-red-700"
              data-testid="admin-validity-clear"
            >
              Remove expiry
            </Button>
            <Button
              type="button"
              onClick={() => saveValidity(validityDate)}
              disabled={validitySaving || !validityDate}
              className="bg-[#0055FF] hover:bg-[#0040CC]"
              data-testid="admin-validity-save"
            >
              {validitySaving ? "Saving…" : "Save validity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shop customer forgot-password rescue — any owner can use this. */}
      <Card className="border-slate-200 shadow-none" data-testid="cust-reset-card">
        <CardHeader className="border-b">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-[#FF5000]" />
            Reset a shop customer&apos;s password
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            When a customer forgets their password, enter their mobile below to
            mint a temporary 8-character password. Share it with them — they
            can log in and immediately set a permanent one.
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <form
            onSubmit={resetShopCustomerPwd}
            className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3"
          >
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="cust-reset-mobile">Customer mobile</Label>
              <Input
                id="cust-reset-mobile"
                inputMode="tel"
                placeholder="9876543210"
                value={custResetMobile}
                onChange={(e) => setCustResetMobile(e.target.value)}
                data-testid="cust-reset-mobile"
              />
            </div>
            <Button
              type="submit"
              disabled={custResetBusy}
              className="bg-[#0055FF] hover:bg-[#0040CC] h-10 whitespace-nowrap"
              data-testid="cust-reset-submit"
            >
              {custResetBusy ? "Resetting…" : "Reset password"}
            </Button>
          </form>

          {custResetResult && (
            <div
              className="rounded-md border border-emerald-200 bg-emerald-50 p-4 space-y-2"
              data-testid="cust-reset-result"
            >
              <div className="text-xs uppercase tracking-widest text-emerald-700 mono">
                One-time password
              </div>
              <div className="flex items-center gap-2">
                <div
                  className="flex-1 rounded bg-white border border-emerald-200 px-3 py-2 mono text-lg font-semibold text-slate-800 select-all"
                  data-testid="cust-reset-temp-pwd"
                >
                  {custResetResult.temp_password}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={copyTempPwd}
                  className="h-10"
                  data-testid="cust-reset-copy"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy
                </Button>
              </div>
              <p className="text-xs text-slate-600">
                Share this with <b>{custResetResult.customer_name || "the customer"}</b>{" "}
                and ask them to sign in and change it from their Account page.
                This password won&apos;t be shown again.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
